import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTRPC } from '@/lib/trpc.js';
import { putToSignedUrl, uploadOne } from './uploadFile.js';
import {
  addFiles,
  type RejectedFile,
  removeRow,
  renameRow,
  settleRow,
  startRow,
  type UploadRow,
} from './uploadRows.js';

export interface PreflightUpload {
  rows: UploadRow[];
  rejected: RejectedFile[];
  onFilesPicked: (files: File[]) => void;
  onRemove: (key: string) => void;
  onRename: (key: string, fileName: string) => void;
}

export function usePreflightUpload(): PreflightUpload {
  const trpc = useTRPC();
  const createLink = useMutation(trpc.uploads.createUploadLink.mutationOptions());
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  // Uploads settle between renders, so React's snapshot would write a settled
  // row back to uploading.
  const latest = useRef<UploadRow[]>([]);

  function commit(update: (current: UploadRow[]) => UploadRow[]): UploadRow[] {
    latest.current = update(latest.current);
    setRows(latest.current);
    return latest.current;
  }

  async function send(row: UploadRow): Promise<void> {
    commit((current) => startRow(current, row.key));
    const outcome = await uploadOne(row, {
      createLink: (input) => createLink.mutateAsync(input),
      put: putToSignedUrl,
    });
    commit((current) => settleRow(current, row.key, outcome));
  }

  return {
    rows,
    rejected,
    onRemove: (key) => {
      commit((current) => removeRow(current, key));
    },
    onRename: (key, fileName) => {
      commit((current) => renameRow(current, key, fileName));
    },
    onFilesPicked: (files) => {
      const added = addFiles(latest.current, files);
      commit(() => added.rows);
      setRejected((seen) => [...seen, ...added.rejected]);
      for (const row of added.rows.filter((entry) => entry.status === 'pending')) {
        void send(row);
      }
    },
  };
}
